import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Check, Clock, Truck, ChefHat, Star, Users, Sparkles, ChevronDown, ChevronUp, Calculator, Zap } from 'lucide-react'
import CateringWizard from './CateringWizard.jsx'
import GalleryLightbox from './GalleryLightbox.jsx'
import SEO from './SEO.jsx'
import useAsset, { getAssetUrl } from '@/hooks/useAsset'

const CateringPage = () => {
  // Force light mode on this page
  useEffect(() => {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('theme', 'light')
  }, [])

  const [openFaq, setOpenFaq] = useState(null)
  const [isVideoLoading, setIsVideoLoading] = useState(true)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    eventDate: '',
    eventLocation: '',
    guestCount: '',
    packageInterest: '',
    additionalDetails: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  // Cache-busted assets using hooks
  const tailtationsLogo = useAsset('/tailtations.svg')
  const lobsterDrawing = useAsset('/lobster-drawing.png')
  const videoSrc = useAsset('/Tailtations_film_720p.mp4')

  // Cache-busted gallery images - generate array outside the render
  const cateringImageUrls = Array.from({ length: 10 }, (_, i) => getAssetUrl(`/images/catering/${i + 1}.webp`))

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Format data to match homepage contact form
      const contactFormData = {
        name: formData.fullName,
        email: formData.email,
        subject: 'Catering Request',
        message: `
Phone: ${formData.phone || 'Not provided'}
Event Date: ${formData.eventDate}
Event Location: ${formData.eventLocation || 'Not provided'}
Number of Guests: ${formData.guestCount}
Package Interest: ${formData.packageInterest || 'Not specified'}

Additional Details:
${formData.additionalDetails || 'None'}
        `.trim()
      }

      const response = await fetch('/.netlify/functions/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactFormData),
      })

      const result = await response.json()

      if (response.ok && result.success) {
        setSubmitStatus('success')
        setTimeout(() => {
          setSubmitStatus(null)
          setFormData({
            fullName: '',
            email: '',
            phone: '',
            eventDate: '',
            eventLocation: '',
            guestCount: '',
            packageInterest: '',
            additionalDetails: ''
          })
        }, 3000)
      } else {
        setSubmitStatus('error')
        console.error('Failed to send catering request:', result.error)
      }
    } catch (error) {
      setSubmitStatus('error')
      console.error('Error submitting form:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const scrollToContact = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })
  }

  const faqs = [
    {
      q: 'What is the minimum order for catering?',
      a: '80 tails, which is our Party Starter package perfect for intimate gatherings of 30-50 guests.'
    },
    {
      q: 'What does the Mobile Kitchen Fee cover?',
      a: 'The $150 Mobile Kitchen Fee covers the cost for our food truck, two professional staff members, gas, and travel to your location. This ensures a seamless, full-service experience from start to finish.'
    },
    {
      q: 'How much space do you need for the truck?',
      a: 'We need approximately 30 feet of flat, accessible space for our food truck with standard vehicle access and clearance.'
    },
    {
      q: 'How far do you travel?',
      a: 'We serve a 30-mile radius from our home base. Events outside this area may incur an additional travel surcharge. Contact us to discuss your location.'
    },
    {
      q: 'What about dietary restrictions?',
      a: 'Please let us know of any allergies or restrictions when you submit your quote request, and we will do our best to accommodate your guests\' needs.'
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Catering - Tailtations® | Food Truck & Catering Trays"
        description="Elevate your next event with Tailtations catering. Use our Instant Price Calculator to get a quote for our food truck or catering tray services."
      />
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-6 flex justify-between items-center">
          <Link to="/">
            <img
              src={tailtationsLogo}
              alt="Tailtations"
              style={{ height: '60px' }}
            />
          </Link>
          <div className="flex gap-3 items-center">
            <button
              onClick={() => setWizardOpen(true)}
              className="hidden sm:flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold border-2 border-[#962831] !text-[#962831] bg-transparent hover:bg-[#962831] hover:!text-white transition-all"
            >
              <Zap className="w-4 h-4" />
              Instant Price Calculator
            </button>
            <button
              onClick={scrollToContact}
              className="bg-[#962831] !text-white px-6 py-3 rounded-md hover:bg-[#962831]/90 transition-colors font-semibold"
            >
              Get a Free Quote
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden min-h-[600px] flex items-center">
        {/* Loading Spinner */}
        {isVideoLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20">
            <img
              src={lobsterDrawing}
              alt="Loading"
              className="w-[150px] h-[150px] animate-spin-slow"
              style={{ animationDirection: 'reverse' }}
            />
            <p className="mt-4 !text-[#962831] font-light text-lg">Loading video...</p>
          </div>
        )}

        {/* Video Background */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          onLoadedData={() => setIsVideoLoading(false)}
          onCanPlayThrough={() => setIsVideoLoading(false)}
          style={{ filter: 'brightness(0.7)' }}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-black/10 to-black/20" />

        <div className="container mx-auto max-w-6xl text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-black mb-6 catering-hero-heading" style={{ fontFamily: 'League Spartan, sans-serif' }}>
            Bring the Tailtations Experience<br />to Your Next Event!
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto catering-hero-subtext">
            Gourmet, Freshly Fried Tails Served On-Site from Our Food Truck.<br />
            Perfect for Parties, Corporate Events, and Weddings.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={scrollToContact}
              className="bg-white px-8 py-4 rounded-lg hover:bg-gray-100 transition-all text-lg font-semibold shadow-xl transform hover:scale-105 !text-[#962831]"
            >
              Get a Free Quote
            </button>
            <button
              onClick={() => setWizardOpen(true)}
              className="flex items-center gap-2 px-8 py-4 rounded-lg font-semibold text-lg border-2 border-white !text-white bg-transparent hover:bg-white hover:!text-[#962831] transition-all shadow-xl transform hover:scale-105"
            >
              <Zap className="w-5 h-5" />
              Instant Price Calculator
            </button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 !text-gray-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#962831]/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold !text-[#962831]">1</span>
              </div>
              <h3 className="text-xl font-bold mb-3 !text-gray-900">Tell Us About Your Event</h3>
              <p className="!text-gray-600">Fill out our quick form to get a custom quote.</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#962831]/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold !text-[#962831]">2</span>
              </div>
              <h3 className="text-xl font-bold mb-3 !text-gray-900">We Handle the Prep</h3>
              <p className="!text-gray-600">Our chefs par-fry your tails before arrival, ensuring quality and speed.</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#962831]/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold !text-[#962831]">3</span>
              </div>
              <h3 className="text-xl font-bold mb-3 !text-gray-900">Enjoy the Party</h3>
              <p className="!text-gray-600">We arrive with our food truck and serve fresh, hot tails in just 1 minute per order.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Catering Packages Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-4 !text-gray-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
            Find the Perfect Package for Your Event
          </h2>
          <p className="text-center !text-gray-600 mb-4 max-w-3xl mx-auto">
            All packages include our on-site food truck, two professional chefs, and all necessary serving supplies.
          </p>
          <p className="text-center text-sm !text-gray-600 mb-6">
            <strong>Mobile Kitchen Fee:</strong> A one-time $150 Mobile Kitchen fee applies to all events. This covers travel, gas, and our dedicated on-site chefs.
          </p>

          {/* Instant Price Calculator CTA */}
          <div className="text-center mb-12">
            <button
              onClick={() => setWizardOpen(true)}
              className="inline-flex items-center gap-3 bg-[#962831] !text-white px-8 py-4 rounded-lg hover:bg-[#962831]/90 transition-all text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Zap className="w-6 h-6" />
              Instant Price Calculator
            </button>
            <p className="text-sm !text-gray-600 mt-3">Know your costs in seconds</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Package 1 */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-transparent hover:border-[#962831] transition-all">
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 text-center">
                <h3 className="text-2xl font-bold mb-2 !text-gray-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
                  The Party Starter
                </h3>
                <p className="text-4xl font-black mb-2 !text-[#962831]">$16</p>
                <p className="text-sm !text-gray-600">per tail</p>
              </div>
              <div className="p-6">
                <p className="text-sm !text-gray-500 mb-4">
                  <strong>Ideal For:</strong> Backyard Parties, Birthdays, Small Gatherings
                </p>
                <p className="text-sm !text-gray-500 mb-4">
                  <strong>Guest Count:</strong> 30-50 Guests (approx.)
                </p>
                <p className="text-sm !text-gray-500 mb-4">
                  <strong>Order Size:</strong> 80-120 Tails
                </p>
                <p className="!text-gray-700 mb-6">
                  The perfect way to elevate your next celebration. We bring the party to you, serving up fresh, gourmet tails that will leave your guests raving.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 !text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm !text-gray-800">Up to 1.5 hours of on-site service</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 !text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm !text-gray-800">Our full-service food truck</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 !text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm !text-gray-800">2 professional chefs</span>
                  </div>
                </div>
                <button
                  onClick={() => setWizardOpen(true)}
                  className="w-full bg-[#962831] !text-white py-3 rounded-md hover:bg-[#962831]/90 transition-colors font-semibold flex items-center justify-center gap-2"
                >
                  <Calculator className="w-4 h-4" />
                  Build My Quote
                </button>
              </div>
            </div>

            {/* Package 2 */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-[#962831] relative">
              <div className="absolute top-0 right-0 bg-[#962831] px-4 py-1 text-xs font-bold !text-white catering-most-popular-badge">
                MOST POPULAR
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 text-center">
                <h3 className="text-2xl font-bold mb-2 !text-gray-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
                  The Corporate Favorite
                </h3>
                <p className="text-4xl font-black mb-2 !text-[#962831]">$15</p>
                <p className="text-sm !text-gray-600">per tail</p>
              </div>
              <div className="p-6">
                <p className="text-sm !text-gray-500 mb-4">
                  <strong>Ideal For:</strong> Company Lunches, Client Events, Team Building
                </p>
                <p className="text-sm !text-gray-500 mb-4">
                  <strong>Guest Count:</strong> 50-100 Guests (approx.)
                </p>
                <p className="text-sm !text-gray-500 mb-4">
                  <strong>Order Size:</strong> 140-240 Tails
                </p>
                <p className="!text-gray-700 mb-6">
                  Forget boring boxed lunches. Reward your team or impress your clients with a memorable, high-quality food truck experience right at your office.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 !text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm !text-gray-800">Up to 2.5 hours of on-site service</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 !text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm !text-gray-800">Our full-service food truck</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 !text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm !text-gray-800">2 professional chefs</span>
                  </div>
                </div>
                <button
                  onClick={() => setWizardOpen(true)}
                  className="w-full bg-[#962831] !text-white py-3 rounded-md hover:bg-[#962831]/90 transition-colors font-semibold flex items-center justify-center gap-2"
                >
                  <Calculator className="w-4 h-4" />
                  Build My Quote
                </button>
              </div>
            </div>

            {/* Package 3 */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-transparent hover:border-[#962831] transition-all">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 text-center">
                <h3 className="text-2xl font-bold mb-2 !text-gray-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
                  The Main Event
                </h3>
                <p className="text-4xl font-black mb-2 !text-[#962831]">$14</p>
                <p className="text-sm !text-gray-600">per tail</p>
              </div>
              <div className="p-6">
                <p className="text-sm !text-gray-500 mb-4">
                  <strong>Ideal For:</strong> Weddings, Large Receptions, Community Festivals
                </p>
                <p className="text-sm !text-gray-500 mb-4">
                  <strong>Guest Count:</strong> 100-200+ Guests
                </p>
                <p className="text-sm !text-gray-500 mb-4">
                  <strong>Order Size:</strong> 250-400+ Tails
                </p>
                <p className="!text-gray-700 mb-6">
                  The ultimate catering solution for large crowds. Our efficient process ensures we can serve hundreds of guests quickly without compromising quality.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 !text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm !text-gray-800">Up to 3.5 hours of on-site service</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 !text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm !text-gray-800">Our full-service food truck</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 !text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm !text-gray-800">2 professional chefs</span>
                  </div>
                </div>
                <button
                  onClick={() => setWizardOpen(true)}
                  className="w-full bg-[#962831] !text-white py-3 rounded-md hover:bg-[#962831]/90 transition-colors font-semibold flex items-center justify-center gap-2"
                >
                  <Calculator className="w-4 h-4" />
                  Build My Quote
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Tailtations Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 !text-gray-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
            Why Tailtations?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#962831]/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 !text-[#962831]" />
              </div>
              <h3 className="text-lg font-bold mb-2 !text-gray-900">Unforgettable Experience</h3>
              <p className="!text-gray-600 text-sm">More than just food, it's a live culinary show. Our vibrant food truck becomes a centerpiece for your event.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#962831]/10 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 !text-[#962831]" />
              </div>
              <h3 className="text-lg font-bold mb-2 !text-gray-900">Incredible Efficiency</h3>
              <p className="!text-gray-600 text-sm">Thanks to our unique par-frying process, we serve guests in about 60 seconds. No long lines, just happy, well-fed people.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#962831]/10 flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 !text-[#962831]" />
              </div>
              <h3 className="text-lg font-bold mb-2 !text-gray-900">Gourmet Quality</h3>
              <p className="!text-gray-600 text-sm">We use only the highest quality ingredients, from our premium tails to our signature sauces. Every bite is a testament to our commitment to flavor.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#962831]/10 flex items-center justify-center mx-auto mb-4">
                <ChefHat className="w-8 h-8 !text-[#962831]" />
              </div>
              <h3 className="text-lg font-bold mb-2 !text-gray-900">Full-Service & Hassle-Free</h3>
              <p className="!text-gray-600 text-sm">We bring everything—the truck, the chefs, the supplies. You just focus on enjoying your event.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold text-center mb-12 !text-gray-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full p-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-lg !text-gray-900">{faq.q}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 !text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 !text-gray-500" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 pt-2 !text-gray-600">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 !text-gray-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
            Trusted by Event Planners Everywhere
          </h2>

          {/* Statistics */}
          <div className="grid md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="text-5xl font-black mb-2 !text-[#962831]">15+</div>
              <p className="!text-gray-600 font-semibold">Events Catered</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black mb-2 !text-[#962831]">10,000+</div>
              <p className="!text-gray-600 font-semibold">Guests Served</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black mb-2 !text-[#962831]">5.0</div>
              <p className="!text-gray-600 font-semibold">Average Rating</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black mb-2 !text-[#962831]">100%</div>
              <p className="!text-gray-600 font-semibold">Satisfaction Rate</p>
            </div>
          </div>

          {/* Testimonials */}
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg shadow">
              <div className="flex mb-4">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 fill-[#962831] !text-[#962831]" />)}
              </div>
              <p className="!text-gray-700 mb-4 italic">"Tailtations made our corporate event unforgettable! The lobster tails were absolutely delicious. Our team is still talking about it weeks later."</p>
              <p className="font-semibold !text-gray-900">Luxury Car Brand</p>
              <p className="text-sm !text-gray-600">300 Guests</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow">
              <div className="flex mb-4">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 fill-[#962831] !text-[#962831]" />)}
              </div>
              <p className="!text-gray-700 mb-4 italic">"Tailtations brought 120 Lobster Tails and our attendees were all smiles. We served our VIP Celebrities, actors and musicians, many more than once."</p>
              <p className="font-semibold !text-gray-900">Award Show VIP Green Room</p>
              <p className="text-sm !text-gray-600">200 Guests</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow">
              <div className="flex mb-4">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 fill-[#962831] !text-[#962831]" />)}
              </div>
              <p className="!text-gray-700 mb-4 italic">"From arrival to the final guest served, working with Tailtations was seamless. They're professional, reliable, and the Tails are next level. Best lobster we have ever eaten! We'll definitely be booking them again. 10/10 experience. Simply a party trick."</p>
              <p className="font-semibold !text-gray-900">Halloween Party</p>
              <p className="text-sm !text-gray-600">900 Guests</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-4 !text-gray-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
            See Us in Action
          </h2>
          <p className="text-center !text-gray-600 mb-12">Real events, real food, real happy guests</p>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {cateringImageUrls.map((imageUrl, index) => (
              <div
                key={index}
                className="aspect-square rounded-lg overflow-hidden group cursor-pointer shadow-md hover:shadow-xl transition-shadow"
                onClick={() => {
                  setLightboxIndex(index);
                  setLightboxOpen(true);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightboxIndex(index);
                    setLightboxOpen(true);
                  }
                }}
                aria-label={`View image ${index + 1} in lightbox`}
              >
                <img
                  src={imageUrl}
                  alt={`Tailtations catering event ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="!text-gray-600 text-sm mb-4">
              All photos from real Tailtations catering events
            </p>
            <button
              onClick={() => window.open('https://www.instagram.com/tailtations', '_blank', 'noopener,noreferrer')}
              className="bg-[#962831] !text-white px-6 py-3 rounded-md hover:bg-[#962831]/90 transition-colors font-semibold inline-flex items-center justify-center gap-2"
            >
              View More on Instagram
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-4xl font-bold text-center mb-4 !text-gray-900" style={{ fontFamily: 'League Spartan, sans-serif' }}>
            Ready to Get Started?
          </h2>
          <p className="text-center !text-gray-600 mb-8">
            Tell us a little about your event, and our catering specialist will get back to you with a personalized quote within 24 hours.
          </p>

          {/* Calculator CTA */}
          <div className="text-center mb-12">
            <p className="text-lg font-semibold !text-gray-700 mb-4">Or, Know Your Costs in Seconds</p>
            <button
              onClick={() => setWizardOpen(true)}
              className="inline-flex items-center gap-3 bg-[#962831] !text-white px-8 py-4 rounded-lg hover:bg-[#962831]/90 transition-all text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Zap className="w-6 h-6" />
              Instant Price Calculator
            </button>
          </div>

          {submitStatus === 'success' ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
              <Check className="w-16 h-16 !text-green-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold !text-green-800 mb-2">Thank You!</h3>
              <p className="!text-green-700">We've received your request and will get back to you within 24 hours with a personalized quote.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="catering-contact-form bg-white rounded-lg shadow-lg p-8">
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-center">
                  <p className="!text-red-700">Sorry, there was an error submitting your request. Please try again or email us directly at catering@tailtations.com</p>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 !text-gray-900">Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#962831]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 !text-gray-900">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#962831]"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 !text-gray-900">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#962831]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 !text-gray-900">Event Date *</label>
                  <input
                    type="date"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#962831]"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 !text-gray-900">Event Location (City/Town)</label>
                  <input
                    type="text"
                    name="eventLocation"
                    value={formData.eventLocation}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#962831]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 !text-gray-900">Estimated Number of Guests *</label>
                  <input
                    type="number"
                    name="guestCount"
                    value={formData.guestCount}
                    onChange={handleChange}
                    required
                    min="30"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#962831]"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 !text-gray-900">Which package are you interested in?</label>
                <select
                  name="packageInterest"
                  value={formData.packageInterest}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#962831]"
                >
                  <option value="">Select a package...</option>
                  <option value="party-starter">The Party Starter</option>
                  <option value="corporate-favorite">The Corporate Favorite</option>
                  <option value="main-event">The Main Event</option>
                  <option value="not-sure">Not Sure Yet</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 !text-gray-900">Additional Details or Questions</label>
                <textarea
                  name="additionalDetails"
                  value={formData.additionalDetails}
                  onChange={handleChange}
                  rows="4"
                  maxLength="500"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#962831]"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#962831] !text-white py-4 rounded-md hover:bg-[#962831]/90 transition-colors text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Request My Quote'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Catering Wizard Modal */}
      <CateringWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
      />

      {/* Gallery Lightbox */}
      {lightboxOpen && (
        <GalleryLightbox
          images={cateringImageUrls}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  )
}

export default CateringPage
